import SockJS from 'sockjs-client';
import omit from 'lodash/omit';
import { v4 as uuid } from 'uuid';

import Channel, {
    channelReusesRequestId,
    RequestInfo,
    toStopDestination,
} from 'communication/Channel';
import Handler from 'communication/Handler';
import IndigoMessage, {
    IndigoRequestMessageType,
} from 'communication/IndigoMessage';
import NetworkState from 'communication/NetworkState';
import {
    SockJsStreamingServiceErrorCodes,
    WellKnownWebsocketErrorCodes,
} from './SockJsStreamingErrorCodes';
import StreamState, { ConnectionStatus } from 'communication/StreamState';
import ResendRequest from './ResendRequest';

import {
    buildLogger,
    ForceLoggingWrapper,
    Logger,
    LoggingService,
    timeUtils,
} from '@instinet-analytics/core-lib';
import {
    addItemToArrayWithMaxSize,
    IndexedArray,
    removeFromIndexedArray,
} from 'utils/arrays/arrayUtils';

import { ChannelDestination } from '../Channel';
import IndigoMalformedMessage from 'communication/IndigoMalformedMessage';
import isNil from 'lodash/isNil';
import IndigoApi from 'communication/IndigoApi';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { IndigoRequestMessage } from 'communication/IndigoRequestMessage';
import { clearWorkerInterval, setWorkerInterval } from 'workers/workerInterval';
import {
    isStreamStatusMessage,
    StreamStatusMessage,
    WellKnownIndigo2MessageType,
} from 'communication/streaming/StreamStatusMessage';
import { NotificationService } from 'notification/NotificationService';
import { StreamStatusMessageAlertManager } from 'communication/streaming/StreamStatusMessageAlertManager';
import { ChannelMessageStorageManager } from 'communication/streaming/ChannelMessageStorageManager';
import { using as usingStatement } from 'using-statement';
import {
    exponentialRetry,
    exponentialRetryFunction,
} from 'utils/retry/useExponentialRetry';
import RetryFunction from 'utils/retry/RetryFunction';
import {
    IndigoInternalMessage,
    IndigoInternalMessageType,
    InternalDestination,
} from 'communication/IndigoInternalMessage';
import LoginResponse from 'communication/LoginResponse';
import StreamingService, {
    SessionDetails,
    StreamingServiceInfo,
} from 'communication/streaming/StreamingService';

const { getCurrentTime } = timeUtils;

export const PostingMessage = 'Posting batchStreamRequest';

export type IndigoStreamRequest = {
    payload: AnyPayload;
    indigo2AsyncDestination: ChannelDestination;
    channelId?: number;
};

type StreamRequestResponse = {
    requestId: number;
    dispatchStatus: 'DISPATCHED' | 'HANDLE_NOT_FOUND' | 'VALIDATION_ERROR';
    responseStatus: number;
};

export type RequestBatchSanitizer = (
    requests: IndigoStreamRequest[]
) => IndigoStreamRequest[];

export interface SockJSStreamingServiceOptions {
    onNetworkStateUpdate: (networkState: NetworkState) => void;
    onUnhandledMalformedMessage: (message: IndigoMalformedMessage) => void;
    onAvailabilityStatusUpdate: (status: {
        availabilityStatus: string;
        available: boolean;
    }) => void;
    onReconnectedToIndigo?: () => void;
    loggingService: LoggingService;
    indigoApi: IndigoApi;
    indigoServiceClient: IndigoServiceClient;
    notificationService: NotificationService;
    requestSanitizers: {
        [channelDestination: string]: RequestBatchSanitizer;
    };
    channelMessageStorageManager: ChannelMessageStorageManager;
}

const MAX_MESSAGES_COUNT = 10;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPayload = any;
type AnyIndigoMessage = IndigoMessage<AnyPayload>;
type AnyMessage =
    | IndigoMalformedMessage
    | AnyIndigoMessage
    | StreamStatusMessage;

const RENEW_INTERVAL_SECS = 30;
const RENEW_CHANNEL_ID = 1;
const FIRST_AVAILABLE_CHANNEL_ID = 2;

export default class SockJSStreamingService implements StreamingService {
    private sessionDetails: SessionDetails | undefined;
    private readonly streamState: StreamState = {
        status: 'Disconnected',
        connectionType: '',
        channels: {},
        unprocessedResponses: [],
        streamStatusMessages: [],
        disposedConnections: [],
    };
    private sockJsConnection?: WebSocket;
    private lastProcessedMessageNumber: number;
    private largestReceivedMessageNumber: number;
    private nextChannelIndex: number;
    private readonly onNetworkStateUpdate: (networkState: NetworkState) => void;
    private readonly onUnhandledMalformedMessage: (
        message: IndigoMalformedMessage
    ) => void;
    private readonly onAvailabilityStatusUpdate: (status: {
        availabilityStatus: string;
        available: boolean;
    }) => void;
    private requestingResend: boolean;
    private loggingService: LoggingService;
    private logger: Logger;
    private pendingMessageCache: IndexedArray<AnyIndigoMessage>;
    private pendingMessageSince: number | undefined;
    public createdDate: Date;
    private indigoApi: IndigoApi;
    private indigoServiceClient: IndigoServiceClient;
    private streamStatusMessageAlertManager: StreamStatusMessageAlertManager;
    private channelMessageStorageManager: ChannelMessageStorageManager;
    private didConnect = false;

    private reconnectRetryManager: RetryFunction | undefined;
    private onReconnectedToIndigo: (() => void) | undefined;
    private renewIntervalId: string | null = null;
    private initialising = false;
    /*
     *Requests send when initialising, or disconnected
     *They will be send when initialisation is complete, or a connection is established
     */
    private queuedRequests: Array<IndigoStreamRequest> = [];
    /*
     * Count of requests batches started by a startRequestBatch() call
     * When the count returns to zero during a commitRequestBatch() call, the batch will be sent
     */
    private requestBatchCount = 0;
    private requestBatch: Array<IndigoStreamRequest> = [];
    private readonly requestSanitizers: {
        [channelDestination: string]: RequestBatchSanitizer;
    } = {};

    public constructor({
        onNetworkStateUpdate,
        onUnhandledMalformedMessage,
        onAvailabilityStatusUpdate,
        onReconnectedToIndigo,
        loggingService,
        indigoApi,
        indigoServiceClient,
        notificationService,
        requestSanitizers,
        channelMessageStorageManager,
    }: SockJSStreamingServiceOptions) {
        this.createdDate = new Date();

        this.indigoApi = indigoApi;
        this.indigoServiceClient = indigoServiceClient;
        this.streamStatusMessageAlertManager =
            new StreamStatusMessageAlertManager({
                notificationService,
                loggingService,
            });

        this.onNetworkStateUpdate = onNetworkStateUpdate;
        this.onUnhandledMalformedMessage = onUnhandledMalformedMessage;
        this.onAvailabilityStatusUpdate = onAvailabilityStatusUpdate;

        this.streamState.status = 'Disconnected';
        this.streamState.connectionType = '';
        this.largestReceivedMessageNumber = 0;
        this.lastProcessedMessageNumber = -1;
        this.nextChannelIndex = FIRST_AVAILABLE_CHANNEL_ID;
        this.requestingResend = false;
        this.loggingService = loggingService;
        this.logger = buildLogger('SockJSStreamingService', loggingService);
        this.pendingMessageCache = [];
        this.requestSanitizers = {
            ...this.requestSanitizers,
            ...requestSanitizers,
        };
        this.channelMessageStorageManager = channelMessageStorageManager;
        this.onReconnectedToIndigo = onReconnectedToIndigo;

        this.logger.info(`Initialised`);
    }

    /*
     * Connects to the Web Socket with an exponential retry
     */
    public async connect(): Promise<void> {
        const attemptConnectWithRetry = exponentialRetry({
            action: this.connectWithoutRetry.bind(this),
            retryOnError: true,
            postTimeoutInterval: 60000,
            retryLog: (durationMs: number) =>
                this.logger.warn(`Will re-attempt connect in ${durationMs}ms`),
        });
        await attemptConnectWithRetry();
        this.logger.debug('Connected');
    }

    public get url() {
        return this.indigoServiceClient.getStreamingUrl();
    }

    public initialiseInternalChannel(userDetails: LoginResponse): void {
        const { connectionId, uid, machineId } = userDetails!;
        this.startStreamRequest(
            { sessionId: uid, connectionId: connectionId, machineId },
            InternalDestination,
            this.handleInternalMessage
        );
    }

    public initSession(sessionDetails: SessionDetails) {
        this.resetWebSocketSession();
        this.sessionDetails = sessionDetails;
        this.logger.info(
            `Initialised session (connectionId: ${sessionDetails.connectionId}, session: ${sessionDetails.session.id})`,
            sessionDetails.session
        );
        this.scheduleRenew();
    }

    private async connectWithoutRetry(): Promise<void> {
        return new Promise<void>((resolve, reject): void => {
            const correlationId = uuid();
            let didOpenConnection = false;

            this.logger.debug('Connecting to SockJS', {
                url: this.url,
                correlationId,
                existingSockJsConnection: !!this.sockJsConnection,
            });

            if (this.sockJsConnection) {
                return reject();
            }

            this.sockJsConnection = new SockJS(this.url);

            this.sockJsConnection.onclose = (ev: CloseEvent): void => {
                if (!didOpenConnection) {
                    // Could not connect...
                    this.logger.warn('Error connecting to SockJS', {
                        url: this.url,
                        ev,
                        correlationId,
                    });

                    const err = { url: this.url, ev };
                    this.disposeConnection('Error connecting');
                    return reject(err);
                }
                // Websocket closed after connected...
                this.onConnectionCloseUnplanned(ev, correlationId);
            };

            this.sockJsConnection.onopen = (): void => {
                didOpenConnection = true;
                this.cancelReconnectRetryManager();

                this.streamState.status = 'Connecting';
                this.streamState.connectionType =
                    this.sockJsConnection!.protocol;

                this.addChannelListener(
                    'renew',
                    () => {
                        return;
                    },
                    RENEW_CHANNEL_ID
                );

                this.logger.debug('Connected', {
                    url: this.url,
                    correlationId,
                });
                resolve();
            };

            this.sockJsConnection.onmessage = (messageEvent): void => {
                this.onMessage(JSON.parse(messageEvent.data));
            };
        });
    }

    public startStreamRequest<T, V>(
        requestPayload: T,
        indigo2AsyncDestination: ChannelDestination,
        handler: Handler<V>,
        channelId?: number
    ): Channel<T, V> {
        try {
            const channel = this.addChannelListener<T, V>(
                indigo2AsyncDestination,
                handler,
                channelId
            );

            this.send(requestPayload, indigo2AsyncDestination, channel.id);
            return channel;
        } catch (error) {
            this.logger.warn('Error starting stream request', error);
            throw error;
        }
    }

    public async rpcRequest<T, V>(
        requestPayload: T,
        destination: ChannelDestination
    ): Promise<V> {
        return new Promise((resolve: (data: V) => void): void => {
            try {
                // eslint-disable-next-line prefer-const
                let channel: Channel<T, V>;

                const handler = (responsePayload: V): void => {
                    this.removeChannelListener(channel.id);
                    resolve(responsePayload);
                };

                channel = this.addOrReuseChannel<T, V>(destination, handler);
                this.send(requestPayload, destination, channel.id);
            } catch (error) {
                this.logger.warn('Error starting rpcRequest', error);
                throw error;
            }
        });
    }

    public stopDataStream(
        channelId: number,
        indigo2AsyncDestination: ChannelDestination,
        removeChannelListener = true
    ): void {
        const indigo2AsyncStopDestination = toStopDestination(
            indigo2AsyncDestination
        );

        try {
            this.send(
                {
                    type: IndigoRequestMessageType.STOP_STREAMING,
                    channel: channelId,
                },
                indigo2AsyncStopDestination,
                channelId
            );
            if (removeChannelListener) {
                this.removeChannelListener(channelId);
            }
        } catch (error) {
            this.logger.warn('Error stopping stream', error);
            throw error;
        }
    }

    /*
     * Clear the user session.
     * The webSocket will remain open, even if the user is logged out
     */
    public clearSession() {
        this.resetWebSocketSession();
        this.indigoServiceClient.clearSession();
    }

    public resetWebSocketSession() {
        this.tryStopAllChannels();
        this.clearRenew();
        this.sessionDetails = undefined;
        this.largestReceivedMessageNumber = 0;
        this.lastProcessedMessageNumber = -1;
        this.disposeStreamState();
        this.streamState.status = 'ConnectedWithoutIndigo';
        this.pendingMessageCache = [];
        this.logger.debug('Cleared session', this.getInfo());
    }

    private async disconnect(): Promise<void> {
        this.logger.warn('Disconnecting', this.getInfo());
        return new Promise<void>((resolve, reject): void => {
            if (this.sockJsConnection) {
                this.sockJsConnection.onclose = (): void => {
                    this.handleConnectionClose({
                        status: 'Disconnected',
                        reason: 'Disconnect',
                    });
                    resolve();
                };
                this.tryStopAllChannels();
                this.clearRenew();
                this.disposeConnection('disconnect');
            } else {
                reject();
            }
        });
    }

    private async handleInternalMessage(
        message: IndigoInternalMessage,
        isError?: boolean
    ) {
        if (!isError) {
            switch (message.payload.type) {
                case IndigoInternalMessageType.SERVER_CHANGED:
                    return await this.handleServerChanged();
            }
        }
    }

    private async handleServerChanged() {
        // grab the current set of channels
        const channels = this.getChannelListeners();

        // close and discard the streaming connection
        await this.disconnect();
        // create a new streaming connection (to the new server)
        if (this.sessionDetails) {
            await this.initSession(this.sessionDetails);
            // add the channel listeners back to the connection
            // invoke the request function on each channel to restart the stream (passing the new server details)
            channels.forEach((channel) => {
                if (channel.latestRequests.length > 0) {
                    const latestRequest = channel.latestRequests[0];
                    this.startStreamRequest(
                        latestRequest.payload,
                        latestRequest.destination,
                        channel.handler
                    );
                }
            });

            //TODO: new init message
        }
    }

    public addOrReuseChannel<T, V>(
        destination: ChannelDestination,
        handler: Handler<V>
    ) {
        for (const channelNumber in Object.keys(this.streamState.channels)) {
            const channel = this.streamState.channels[channelNumber];
            if (
                channel &&
                channel.destination === destination &&
                !channel.isActive
            ) {
                channel.isActive = true;
                this.logger.debug(
                    `Re-using channel ${channel.id} for ${destination}`
                );
                this.addChannelListener<T, V>(destination, handler, channel.id);
                return channel;
            }
        }
        return this.addChannelListener<T, V>(destination, handler);
    }

    public addChannelListener<T, V>(
        destination: ChannelDestination,
        handler: Handler<V>,
        channelId?: number
    ): Channel<T, V> {
        let channel =
            channelId !== undefined && this.streamState.channels[channelId];

        if (!channel) {
            let id;
            if (channelId !== undefined) {
                id = channelId;
            } else {
                while (this.streamState.channels[this.nextChannelIndex]) {
                    this.nextChannelIndex++;
                }

                id = this.nextChannelIndex++;
            }

            channel = {
                id,
                isActive: true,
                destination,
                handler,
                createdDate: new Date(),
                sentCount: 0,
                latestRequests: [],
            };

            this.streamState.channels[channel.id] = channel;
        } else {
            channel.isActive = true;
            channel.handler = handler;
        }

        return channel;
    }

    public removeChannelListener(channelId: number): void {
        if (this.streamState.channels[channelId]) {
            this.streamState.channels[channelId].isActive = false;
        }
    }

    public tryStopAllChannels() {
        const stopRequests: IndigoStreamRequest[] = this.getChannelListeners()
            .filter((l) => l.destination.startsWith('start'))
            .map((l) => ({
                payload: {
                    type: IndigoRequestMessageType.STOP_STREAMING,
                    channel: l.id,
                },
                channelId: l.id,
                indigo2AsyncDestination: toStopDestination(l.destination),
            }));

        try {
            this.sendBatch(stopRequests);
            this.logger.info('Send stop stream requests', stopRequests);
        } catch (err) {
            this.logger.warn('Could not send stop stream request', {
                stopRequests,
                err,
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getChannelListeners(): Channel<any, any>[] {
        return Object.values(this.streamState.channels).filter(
            (c) => c.isActive
        );
    }

    public getInfo(): StreamingServiceInfo {
        return {
            connectionType: this.streamState.connectionType,
            status: this.streamState.status,
            createdDate: this.createdDate,
            sessionDetails: this.sessionDetails,
            url: this.url,
            channels: Object.values(this.streamState.channels).map((c) => {
                return {
                    ...omit(c, 'handler'),
                    destination:
                        c.destination ?? c.latestRequests[0]?.destination,
                };
            }),
            disposedConnections: this.streamState.disposedConnections,
            unprocessedResponses: this.streamState.unprocessedResponses,
            streamStatusMessages: this.streamState.streamStatusMessages,
            lastProcessedMessageNumber: this.lastProcessedMessageNumber,
            largestReceivedMessageNumber: this.largestReceivedMessageNumber,
            initialising: this.initialising,
            initialisationQueueCount: this.queuedRequests.length,
            pendingMessages: {
                count: this.pendingMessageCache.length,
                max: Math.max(
                    ...this.pendingMessageCache.map(
                        (c) => c?.messageNumber ?? -1
                    )
                ),
            },
        };
    }

    public send<T>(
        payload: T,
        indigo2AsyncDestination: ChannelDestination,
        channelId = 0
    ): void {
        if (indigo2AsyncDestination === 'INIT') {
            const { requestInfo, message } = this.buildRequestInfoAndMessage({
                payload,
                indigo2AsyncDestination,
                channelId,
            });
            this.startInitialisation(message, requestInfo);
            return;
        }

        this.sendBatch([{ payload, indigo2AsyncDestination, channelId }]);
    }

    private sendBatch(
        batchedRequests: IndigoStreamRequest[],
        forceDuringInitialisation = false
    ): void {
        if (!this.sockJsConnection) {
            return;
        }

        const requests =
            batchedRequests.length > 1
                ? this.sanitizeBatch(batchedRequests)
                : batchedRequests;

        if (requests.length === 0) {
            return;
        }

        if (!forceDuringInitialisation) {
            if (this.initialising) {
                if (requests.length > 0) {
                    this.queuedRequests.push(...requests);
                    this.logger.debug(
                        `${requests.length} request(s) queued during initialisation`,
                        { queuedRequests: this.queuedRequests }
                    );
                }
                return;
            }

            if (!this.sessionDetails) {
                this.queuedRequests.push(...requests);
                this.logger.debug(
                    `${requests.length} request(s) queued whilst disconnected`,
                    { queuedRequests: this.queuedRequests }
                );
                return;
            }
        }

        if (this.requestBatchCount > 0) {
            this.requestBatch.push(...requests);
            this.logger.debug(
                `Current batch is now ${this.requestBatch
                    .map((r) => `${r.indigo2AsyncDestination}-${r.channelId}`)
                    .join(',')}`
            );
            return;
        }

        const requestInfos: Array<RequestInfo<unknown, unknown>> = [];
        const messages: Array<IndigoRequestMessage<unknown>> = [];

        const sentTime = new Date();

        for (const request of requests) {
            const { requestInfo, message } = this.buildRequestInfoAndMessage(
                request,
                sentTime
            );
            requestInfos.push(requestInfo);
            messages.push(message);
        }

        usingStatement(new ForceLoggingWrapper(this.loggingService), () => {
            this.logger.info(PostingMessage, messages);
        });

        this.indigoServiceClient
            .post<IndigoRequestMessage<unknown>[], StreamRequestResponse[]>(
                'batchStreamRequest',
                messages
            )
            .then((response) => {
                if (response.length !== messages.length) {
                    this.logger.warn(
                        `Number of responses (${response.length}) does not match number of request messages (${messages.length}) in batch`
                    );
                }

                for (let i = 0; i < messages.length; i++) {
                    const requestInfo = requestInfos[i];
                    const messageResponse = response[i];

                    if (messageResponse.dispatchStatus === 'DISPATCHED') {
                        requestInfo.status = 'Sent';
                        this.logger.debug(
                            `Posted stream request ${requestInfo.destination} for requestId ${requestInfo.id}`,
                            requestInfo.correlationId
                        );
                    } else {
                        requestInfo.status = 'Error';
                        this.logger.critical(
                            `Error response for ${requestInfo.destination} requestId ${requestInfo.id}`,
                            messageResponse
                        );
                    }
                }

                this.scheduleRenew();
            })
            .catch((err) => {
                for (const requestInfo of requestInfos) {
                    requestInfo.status = 'Error';
                    this.logger.critical(
                        `Error posting batchStreamRequest ${requestInfo.destination} for requestId ${requestInfo.id}`,
                        err
                    );
                }
            })
            .finally(() => {
                this.scheduleRenew();
            });
    }

    public startRequestBatch() {
        this.requestBatchCount++;
    }

    public commitRequestBatch() {
        this.requestBatchCount--;
        if (this.requestBatchCount < 0) {
            this.requestBatchCount = 0;
        }
        if (this.requestBatchCount === 0) {
            this.sendBatch(this.requestBatch);
            this.requestBatch = [];
        }
        return this.requestBatch;
    }

    public resend(from: number, count: number): void {
        const request: ResendRequest = {
            from: from,
            count: count,
        };

        // eslint-disable-next-line prefer-const
        let channel: Channel<ResendRequest, void>;

        const handler = (): void => {
            this.removeChannelListener(channel.id);
        };

        channel = this.addChannelListener('resend', handler);
        this.send(request, 'resend');
    }

    private haveAlreadyProcessed<T>(message: IndigoMessage<T>): boolean {
        return message.messageNumber <= this.lastProcessedMessageNumber;
    }
    private isNextExpectedMessage<T>(message: IndigoMessage<T>): boolean {
        return message.messageNumber === this.lastProcessedMessageNumber + 1;
    }

    public processMessage<T>(message: IndigoMessage<T>): void {
        this.requestingResend = false;
        this.lastProcessedMessageNumber = message.messageNumber;

        if (this.isInitialisationAckResponse(message)) {
            this.setInitialised(message);
            return;
        }
        if (message.requestId === -1) {
            this.storeUnprocessedResponse('Negative request id', message);
            return;
        }

        this.passToChannel(message);

        // Check whether we have pending messages after this one...
        const pendingMessage =
            this.pendingMessageCache[message.messageNumber + 1];
        if (pendingMessage !== undefined) {
            this.logger.debug(
                'Processing pending message',
                pendingMessage.messageNumber
            );
            removeFromIndexedArray(
                this.pendingMessageCache,
                message.messageNumber + 1
            );
            if (this.pendingMessageCache.length === 0) {
                this.pendingMessageSince = undefined;
            }
            this.processMessage(pendingMessage);
        }
    }

    public passToChannel<T>(message: IndigoMessage<T>) {
        if (message.channelId < 0) {
            this.handleSystemMessage(message);
            return;
        }

        const channel = this.getChannelById(message.channelId);
        if (!channel) {
            this.logger.warn('No channel found', message);
            this.storeUnprocessedResponse('No channel found', message);
            return;
        }

        const latestRequest = channel.latestRequests[0];
        if (latestRequest.id !== message.requestId) {
            this.logger.debug('Received message for older request id', {
                latestRequest,
                message,
            });
            this.storeUnprocessedResponse(
                'Message for older request id',
                message
            );
            return;
        }

        this.channelMessageStorageManager.recordResponse(channel, message);

        channel.handler(message.payload);
    }

    private storeUnprocessedResponse(reason: string, message: AnyMessage) {
        this.streamState.unprocessedResponses = addItemToArrayWithMaxSize(
            {
                receivedTime: new Date(getCurrentTime()),
                reason,
                message,
                messageNumber: (message as IndigoMessage<unknown>)
                    .messageNumber,
            },
            this.streamState.unprocessedResponses,
            MAX_MESSAGES_COUNT
        );
    }

    /**
     * Primitive resend logic - we will send the request a few times with a small delay
     */
    private async attemptResend() {
        for (let i = 0; i < 3; i++) {
            const from = this.lastProcessedMessageNumber + 1;
            const count =
                this.largestReceivedMessageNumber -
                this.lastProcessedMessageNumber;
            this.resend(from, count);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            if (!this.requestingResend) return;
        }

        await this.disconnect();
        this.onNetworkStateUpdate({
            connected: false,
            didConnect: this.didConnect,
            errorCode: SockJsStreamingServiceErrorCodes.ResendFailed,
        });
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    private onMessage<T>(rawMessage: any): void {
        if (!SockJSStreamingService.isValidDataMessageFormat(rawMessage)) {
            this.handleMalformedMessage(rawMessage);
            return;
        }

        if (isStreamStatusMessage(rawMessage)) {
            this.handleStreamStatusMessage(rawMessage);
            return;
        }

        const message: IndigoMessage<T> = rawMessage;

        if (
            message.connectionId !== -1 &&
            message.connectionId !== this.sessionDetails?.connectionId
        ) {
            this.logger.debug(
                `Ignoring message for previous connection (current connection id is ${this.sessionDetails?.connectionId})`,
                message
            );
            this.storeUnprocessedResponse(
                `Message is for previous connection (current connection id is ${this.sessionDetails?.connectionId}) `,
                message
            );
            return;
        }

        if (this.haveAlreadyProcessed(message)) {
            this.logger.debug(
                'Not processing message (already processed)',
                message
            );
            this.storeUnprocessedResponse('Already processed', message);
            return;
        }

        this.largestReceivedMessageNumber = Math.max(
            message.messageNumber,
            this.largestReceivedMessageNumber
        );

        if (!this.isNextExpectedMessage(message)) {
            if (
                this.pendingMessageSince !== undefined &&
                getCurrentTime() - this.pendingMessageSince > 1000
            ) {
                const logMsg = {
                    message,
                    lastProcessedMessage: this.lastProcessedMessageNumber,
                };

                // We have been missing message for more then a second => try a resend (unless already are)
                this.logger.debug(
                    'Waiting for message for longer than one second',
                    logMsg
                );

                if (this.requestingResend) {
                    this.logger.debug('Already requesting resend', logMsg);
                    return;
                }
                this.requestingResend = true;
                this.logger.debug('Attempting resend', logMsg);
                // noinspection JSIgnoredPromiseFromCall
                this.attemptResend();
                return;
            }

            // Place into pending messages so we can process it once the gap is filled...
            if (this.pendingMessageCache.length === 0) {
                // Remember from when we have been waiting for a missing message
                this.pendingMessageSince = getCurrentTime();
            }
            this.pendingMessageCache[message.messageNumber] = message;

            return;
        }

        this.processMessage(message);
    }

    private handleMalformedMessage(rawMessage: IndigoMalformedMessage) {
        this.logger.warn(
            'Received message with incorrect data message format',
            rawMessage
        );
        this.storeUnprocessedResponse('Malformed message', rawMessage);

        const channelId = this.tryGetChannelId(rawMessage);

        if (!isNil(channelId)) {
            const channel = this.getChannelById(channelId);
            if (channel) {
                channel.handler(rawMessage, true);
                return;
            }
        }

        this.onUnhandledMalformedMessage(rawMessage);
    }

    private tryGetChannelId(
        rawMessage: IndigoMalformedMessage
    ): number | undefined {
        if (rawMessage.channelId !== undefined) {
            return rawMessage.channelId;
        }
        return rawMessage.details?.[0]?.channelId;
    }

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    private static isValidDataMessageFormat(message: any): boolean {
        if (message.source && message.source === 'Indigo2') {
            return true;
        }
        return (
            typeof message.channelId === 'number' &&
            (message.channelId < 0 || message.payload !== undefined)
        );
    }

    private async onConnectionCloseUnplanned(
        ev: CloseEvent,
        correlationId: string
    ): Promise<void> {
        this.logger.warn('Unplanned connection closed', {
            ev,
            correlationId,
            info: this.getInfo(),
            attemptingReconnect: !!this.reconnectRetryManager,
        });

        this.handleConnectionClose({
            status: 'Disconnected',
            reason: 'Unplanned connection close: ' + ev.reason,
            errorCode: ev.code,
        });
        this.disposeConnection('Unplanned connection closed');

        try {
            await this.indigoApi.pause();
        } catch (error) {
            this.logger.warn('Error calling remote pause endpoint', error);
        }

        if (!this.reconnectRetryManager) {
            await this.reconnect();
        }
    }

    private async reconnect() {
        if (this.reconnectRetryManager) {
            // Already retrying...
            return;
        }

        this.reconnectRetryManager = exponentialRetryFunction({
            action: this.reconnectWithoutRetry.bind(this),
            retryOnError: true,
            postTimeoutInterval: 60000,
            retryLog: (durationMs: number) =>
                this.logger.warn(
                    `Will re-attempt reconnect in ${durationMs}ms`
                ),
        });
        await this.reconnectRetryManager.exec();
        this.cancelReconnectRetryManager();
    }

    private cancelReconnectRetryManager() {
        if (this.reconnectRetryManager) {
            this.reconnectRetryManager.cancel();
            this.reconnectRetryManager = undefined;
        }
    }

    private async reconnectWithoutRetry() {
        try {
            await this.connectWithoutRetry();
        } catch (error) {
            this.logger.warn('Error re-connecting to websocket', error);
            throw error;
        }
    }

    private handleConnectionClose(prms: {
        status: ConnectionStatus;
        reason?: string;
        errorCode?: number;
    }) {
        const { status, reason, errorCode } = prms;

        this.logger.warn('Connection Closed: ' + reason, { status, errorCode });

        this.clearSession();
        this.streamState.status = status;
        this.onNetworkStateUpdate({
            connected: false,
            didConnect: this.didConnect,
            errorCode,
            reason,
        });
    }

    private handleConnected(reason: string) {
        if (this.streamState.status === 'Connected') {
            return;
        }

        this.streamState.status = 'Connected';
        this.didConnect = true;

        this.onNetworkStateUpdate({
            connected: true,
            didConnect: this.didConnect,
            reason,
        });
    }

    private disposeConnection(reason: string): void {
        if (this.sockJsConnection) {
            this.logger.warn('Disposing sockJsConnection: ' + reason);
            try {
                this.sockJsConnection.close();
            } catch (err) {
                // Close
            }
            this.sockJsConnection = undefined;
        }
    }

    private getChannelById(channelId: number) {
        const channel = this.streamState.channels[channelId];
        return channel && channel.isActive ? channel : undefined;
    }

    private disposeStreamState(): void {
        if (Object.values(this.streamState.channels).length > 0) {
            this.streamState.disposedConnections.push({
                disposedDate: new Date(),
                channels: Object.values(this.streamState.channels).map((c) =>
                    omit(c, ['handler'])
                ),
            });
            this.channelMessageStorageManager.purgeOldConnections(
                this.streamState
            );
        }
        const renew = this.streamState.channels[RENEW_CHANNEL_ID];
        this.streamState.channels = { [RENEW_CHANNEL_ID]: renew };
    }

    private scheduleRenew() {
        this.clearRenew();

        if (!this.sessionDetails) {
            // Can only send renew when authenticated
            return;
        }

        this.renewIntervalId = setWorkerInterval(() => {
            this.sendRenew();
        }, RENEW_INTERVAL_SECS * 1000);
    }

    private clearRenew() {
        if (this.renewIntervalId) {
            clearWorkerInterval(this.renewIntervalId);
        }
        this.renewIntervalId = null;
    }

    private sendRenew() {
        this.logger.info(
            `Sending renew (connectionId: ${this.sessionDetails?.connectionId}, session: ${this.sessionDetails?.session})`
        );
        this.send({}, 'renew', RENEW_CHANNEL_ID);
    }

    private handleSystemMessage<T>(message: IndigoMessage<T>) {
        let reason = 'System Message';

        if (message.requestId === 1) {
            // Response to INIT request
            this.logger.debug('Connection established', message);
            reason = 'INIT';
            this.handleConnected(reason);
        } else if (message.requestId === 2) {
            reason = 'Connection Closing (session invalid)';
            if (this.sessionDetails) {
                this.handleConnectionClose({
                    status: 'ConnectedWithoutIndigo',
                    reason: 'Indigo1 stream connection closing due to session timeout or invalidation',
                    errorCode:
                        SockJsStreamingServiceErrorCodes.Indigo1SessionTimeoutOrInvalidation,
                });
            } else {
                this.logger.debug(
                    'Ignoring session closing message as already logged out',
                    message
                );
            }
        } else if (message.requestId === 3) {
            reason = 'Connection Closing (Indigo1 shutdown)';
            this.handleConnectionClose({
                status: 'ConnectedWithoutIndigo',
                reason: 'Indigo1 stream connection closing due to server shutting down',
                errorCode:
                    SockJsStreamingServiceErrorCodes.Indigo1ServerShuttingDown,
            });
        } else {
            this.logger.debug('Ignoring system message', message);
        }

        this.storeUnprocessedResponse(reason, message);
    }

    private startInitialisation<T>(
        message: IndigoRequestMessage<T>,
        requestInfo: RequestInfo<T, unknown>
    ) {
        this.initialising = true;

        this.sockJsConnection!.send(JSON.stringify(message));
        requestInfo.status = 'Sent';

        this.logger.debug('Initialisation message sent');
    }

    private isInitialisationAckResponse(message: AnyIndigoMessage) {
        return message.channelId === -3;
    }

    private setInitialised(message: AnyIndigoMessage) {
        this.logger.debug('Initialisation complete', {
            queuedRequests: this.queuedRequests,
        });

        this.storeUnprocessedResponse('INIT', message);

        this.sendBatch(this.queuedRequests, true);

        this.initialising = false;
        this.queuedRequests = [];
    }

    private buildRequestInfoAndMessage(
        r: IndigoStreamRequest,
        sentTime: Date = new Date()
    ) {
        const correlationId = uuid();
        const requestInfo: RequestInfo<unknown, unknown> = {
            id: 1,
            destination: r.indigo2AsyncDestination,
            payload: r.payload,
            correlationId,
            sentTime,
            receivedCount: 0,
            latestResponses: [],
            status: 'Sending',
        };

        const channelId = r.channelId ?? 0;
        const channel = this.getChannelById(channelId);
        if (channel) {
            if (!channel.firstSentTime) {
                channel.firstSentTime = sentTime;
            }
            channel.lastSentTime = sentTime;

            channel.sentCount = channel.sentCount + 1;
            if (!channelReusesRequestId(r.indigo2AsyncDestination)) {
                requestInfo.id = channel.sentCount;
            }

            this.channelMessageStorageManager.recordRequest(
                channel,
                requestInfo
            );
        }

        const message: IndigoRequestMessage<unknown> = {
            indigo2AsyncDestination: r.indigo2AsyncDestination,
            correlationId: requestInfo.correlationId,
            connection: {
                messageNumber: this.lastProcessedMessageNumber,
                channelId,
            },
            requestId: requestInfo.id,
            payload: r.payload,
        };

        if (this.sessionDetails) {
            const { session, uid, connectionId } = this.sessionDetails;
            message.sessionId = session;
            message.uid = uid;
            message.connection.connectionId = connectionId;
        }

        return { requestInfo, message };
    }

    public registerRequestBatchSanitizer(
        channelDestination: string,
        sanitizer: RequestBatchSanitizer
    ): void {
        this.requestSanitizers[channelDestination] = sanitizer;
    }

    private sanitizeBatch(
        batchedRequests: IndigoStreamRequest[]
    ): IndigoStreamRequest[] {
        let ret: IndigoStreamRequest[] = [...batchedRequests];
        const isForChannel = (r: IndigoStreamRequest, ch: string) =>
            ch === r.indigo2AsyncDestination ||
            toStopDestination(ch) === r.indigo2AsyncDestination;

        for (const sanitizedChannel of Object.keys(this.requestSanitizers)) {
            const requests = batchedRequests.filter((r) =>
                isForChannel(r, sanitizedChannel)
            );
            if (requests.length > 0) {
                ret = ret.filter((r) => !isForChannel(r, sanitizedChannel));
                const sanitized =
                    this.requestSanitizers[sanitizedChannel](requests);

                if (sanitized.length > 0) {
                    ret.push(...sanitized);
                }
            }
        }
        if (batchedRequests.length !== ret.length) {
            this.logger.debug('Sanitized', {
                before: batchedRequests,
                after: ret,
            });
        }
        return ret;
    }

    private handleStreamStatusMessage(message: StreamStatusMessage) {
        this.streamState.streamStatusMessages = addItemToArrayWithMaxSize(
            {
                receivedTime: new Date(getCurrentTime()),
                message,
            },
            this.streamState.streamStatusMessages,
            100
        );

        switch (message.type) {
            case WellKnownIndigo2MessageType.AUTHORITY:
            case WellKnownIndigo2MessageType.INFO:
                this.logger.debug('No processing needed for message', message);
                return;
            case WellKnownIndigo2MessageType.AVAILABILITY_STATUS: {
                let available = true;
                switch (message.payload.status) {
                    case 'INDIGO1_NOT_READY':
                    case 'INDIGO1_NOT_REACHABLE':
                    case 'INDIGO1_NOT_KNOWN':
                    case 'INDIGO1_ERROR':
                    case 'INDIGO2_NOT_READY': {
                        this.handleConnectionClose({
                            status: 'ConnectedWithoutIndigo',
                            reason:
                                'Availability status ' + message.payload.status,
                        });
                        available = false;
                        break;
                    }
                    case 'READY': {
                        this.onNetworkStateUpdate({
                            connected: true,
                            reason: 'Availability Status "Ready" received',
                            didConnect: true,
                        });
                        if (
                            this.didConnect &&
                            this.onReconnectedToIndigo &&
                            !this.sessionDetails
                        ) {
                            this.logger.debug(
                                'Emitting reconnect on receipt of Availability Status "Ready"'
                            );

                            this.onReconnectedToIndigo();
                        }

                        // No action: consumer should handle availability status update and ask for login details...
                        break;
                    }
                    case 'NOT_SET':
                    case 'INDIGO2_NOT_REACHABLE':
                    case 'READY_RESTART_PENDING':
                    case 'HOLDING_PAGE': {
                        // Ignore
                        break;
                    }
                    default: {
                        this.logger.warn(
                            'Unknown available status received: ' +
                                message.payload.status
                        );
                    }
                }

                this.onAvailabilityStatusUpdate({
                    availabilityStatus: message.payload.status,
                    available,
                });
                return;
            }
            default: {
                switch (message.payload.code) {
                    case WellKnownWebsocketErrorCodes.InitError:
                    case WellKnownWebsocketErrorCodes.SessionAuthError:
                    case WellKnownWebsocketErrorCodes.Indigo1StreamInitError:
                    case WellKnownWebsocketErrorCodes.TransportError:
                    case WellKnownWebsocketErrorCodes.Indigo1StreamLost: {
                        this.handleConnectionClose({
                            status: 'ConnectedWithoutIndigo',
                            reason: 'Stream status message',
                            errorCode: message.payload.code,
                        });
                        return;
                    }
                    default: {
                        this.logger.warn(
                            'Alerting user for stream status message',
                            message
                        );
                        return this.streamStatusMessageAlertManager.alert(
                            message.payload.code
                        );
                    }
                }
            }
        }
    }
}
